/* area-calculator.js
   Minimal, framework-free modal + area math + page scroll lock.
   Expects:
   - A product container with [data-product-section] and dataset.coverage (m² per selling unit)
   - Quantity input with [data-quantity-input]
*/

(function () {
  // Helpers
  const qsa = (root, sel) => Array.from(root.querySelectorAll(sel));
  const parseNum = (v) => {
    if (typeof v !== 'string') return NaN;
    // Accept both "12,34" and "12.34"
    const s = v.trim().replace(/\s+/g, '').replace(',', '.');
    return s === '' ? NaN : Number(s);
  };

  // Unit conversion to meters
  const toMeters = {
    m: (n) => n,
    cm: (n) => n / 100,
    mm: (n) => n / 1000,
    ft: (n) => n * 0.3048,
    in: (n) => n * 0.0254,
    yd: (n) => n * 0.9144,
  };

  // Modal bootstrapping (one per page)
  const modals = qsa(document, '[data-area-calc]');
  if (!modals.length) return;

  // Find the product section (for coverage + quantity input)
  const productSection = document.querySelector('[data-product-section]');
  const coveragePerUnit = Number(productSection?.dataset.coverage || 0); // m² per pack/tile
  const unitLabel = productSection?.dataset.unitType || 'Unit';

  // ------- Page scroll lock (robust & iOS-safe) -------
  let pageScrollTop = 0;
  function lockPageScroll() {
    pageScrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    // Freeze the body in place
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${pageScrollTop}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none'; // blocks touch scrolling on mobile
  }
  function unlockPageScroll() {
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    body.style.touchAction = '';
    window.scrollTo(0, pageScrollTop);
  }

  // Attach to all modals (if multiple sections exist)
  modals.forEach((modal) => initModal(modal));

  function initModal(modal) {
    const sectionId = modal.getAttribute('data-section-id') || '';
    const trigger = document.querySelector(`[data-area-calc-open][aria-controls="${modal.id}"]`);
    const body = modal.querySelector('[data-area-calc-body]');
    const backdrop = modal.querySelector('[data-area-calc-backdrop]');
    const closeButtons = qsa(modal, '[data-area-calc-close]');
    const applyBtn = modal.querySelector('[data-apply]');
    const wastage = modal.querySelector('[data-wastage]');
    const grandEl = modal.querySelector('[data-grand-total]');
    const coverageHint = modal.querySelector('[data-coverage-hint]');

    // Two groups: walls and floors
    const groups = ['walls', 'floors'].map((g) => {
      const root = modal.querySelector(`[data-area-group="${g}"]`);
      return {
        name: g,
        rowsWrap: root.querySelector('[data-rows]'),
        obsWrap: root.querySelector('[data-rows-obstacles]'),
        addBtn: root.querySelector('[data-add-row]'),
        addObsBtn: root.querySelector('[data-add-row-obstacle]'),
        subtotalEl: root.querySelector(`[data-subtotal="${g}"]`),
      };
    });

    // Insert 1 default row for each group
    const rowTpl = modal.querySelector('template[data-row-template]');
    const makeRow = () => rowTpl.content.firstElementChild.cloneNode(true);

    groups.forEach((g) => {
      g.addBtn.addEventListener('click', () => {
        g.rowsWrap.appendChild(makeRow());
        recalc();
      });
      g.addObsBtn.addEventListener('click', () => {
        g.obsWrap.appendChild(makeRow());
        recalc();
      });
      // start with one row for convenience
      g.rowsWrap.appendChild(makeRow());
    });

    // Delegate remove + input changes
    modal.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove]');
      if (btn) {
        const row = btn.closest('[data-row]');
        row?.remove();
        recalc();
      }
    });

    modal.addEventListener('input', (e) => {
      if (e.target.matches('input, select')) recalc();
    });

    // ----- Open/close behavior + scroll lock -----
    trigger?.addEventListener('click', () => openModal());
    backdrop?.addEventListener('click', () => closeModal());
    closeButtons.forEach((b) => b.addEventListener('click', () => closeModal()));
    document.addEventListener('keydown', (e) => {
      if (modal.dataset.open === 'true' && e.key === 'Escape') closeModal();
    });

    function openModal() {
      modal.dataset.open = 'true';
      modal.setAttribute('aria-hidden', 'false');

      // Lock page scroll immediately
      lockPageScroll();

      // Make sure modal content actually scrolls (and contains scroll chaining)
      if (body) {
        body.style.overscrollBehavior = 'contain'; // blocks bounce to page
      }
      modal.style.overscrollBehavior = 'contain';

      coverageHint.textContent = coveragePerUnit > 0
        ? `Coverage per ${unitLabel}: ${coveragePerUnit} m²`
        : '';
      recalc();

      // focus first input
      const firstInput = modal.querySelector('[data-row] input');
      firstInput?.focus();

      // Extra guard: prevent wheel/touch from leaking when at edges
      attachScrollGuards(body);
    }

    function closeModal() {
      modal.dataset.open = 'false';
      modal.setAttribute('aria-hidden', 'true');
      // Release page lock
      unlockPageScroll();
      // Clean guards
      detachScrollGuards(body);
      trigger?.focus();
    }

    // Compute areas
    function sumArea(rowsWrap) {
      return qsa(rowsWrap, '[data-row]').reduce((acc, row) => {
        const [a, , b] = qsa(row, 'input, span, input'); // two inputs around the × span
        const unitSel = row.querySelector('select');
        const n1 = parseNum(a.value);
        const n2 = parseNum(b.value);
        const unit = unitSel?.value || 'm';
        if (isNaN(n1) || isNaN(n2) || !toMeters[unit]) return acc;
        const lengthM = toMeters[unit](n1);
        const widthM  = toMeters[unit](n2);
        const area = lengthM * widthM;
        return area > 0 ? acc + area : acc;
      }, 0);
    }

    function recalc() {
      const wallGroup = groups[0];
      const floorGroup = groups[1];

      const wallsArea = Math.max(0, sumArea(wallGroup.rowsWrap) - sumArea(wallGroup.obsWrap));
      const floorsArea = Math.max(0, sumArea(floorGroup.rowsWrap) - sumArea(floorGroup.obsWrap));

      let total = wallsArea + floorsArea;
      if (wastage?.checked) total *= 1.10;

      wallGroup.subtotalEl.textContent = wallsArea.toFixed(2);
      floorGroup.subtotalEl.textContent = floorsArea.toFixed(2);
      grandEl.textContent = total.toFixed(2);

      // Optional live coverage hint for needed packs (informational)
      if (coveragePerUnit > 0) {
        const units = Math.ceil(total / coveragePerUnit);
        coverageHint.textContent =
          `Coverage per ${unitLabel}: ${coveragePerUnit} m² — You’ll need ~${units} ${unitLabel.toLowerCase()}(s).`;
      }
    }

    // Apply → set product quantity based on coverage
    applyBtn?.addEventListener('click', () => {
      const total = Number(grandEl.textContent.replace(',', '.')) || 0;
      if (total <= 0 || coveragePerUnit <= 0) {
        closeModal();
        return;
      }
      const unitsNeeded = Math.ceil(total / coveragePerUnit);

      // Find the theme’s quantity input for this section
      const qty = document.querySelector(`#Quantity-${sectionId}`) ||
                  document.querySelector('[data-quantity-input]');
      if (qty) {
        qty.value = String(unitsNeeded);
        // Fire events so any listeners update prices/availability
        qty.dispatchEvent(new Event('input', { bubbles: true }));
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      }
      closeModal();
    });

    // ---------- Scroll-bleed guards on the modal content ----------
    let wheelGuard, touchStartGuard, touchMoveGuard, touchStartY = 0;

    function attachScrollGuards(scroller) {
      if (!scroller) return;

      wheelGuard = function (e) {
        const atTop = scroller.scrollTop === 0 && e.deltaY < 0;
        const atBottom =
          Math.ceil(scroller.scrollTop + scroller.clientHeight) >= scroller.scrollHeight &&
          e.deltaY > 0;
        if (atTop || atBottom) e.preventDefault();
      };
      scroller.addEventListener('wheel', wheelGuard, { passive: false });

      touchStartGuard = function (e) {
        if (e.touches && e.touches[0]) touchStartY = e.touches[0].clientY;
      };
      scroller.addEventListener('touchstart', touchStartGuard, { passive: true });

      touchMoveGuard = function (e) {
        if (!(e.touches && e.touches[0])) return;
        const dy = touchStartY - e.touches[0].clientY;
        const atTop = scroller.scrollTop === 0 && dy < 0;
        const atBottom =
          Math.ceil(scroller.scrollTop + scroller.clientHeight) >= scroller.scrollHeight &&
          dy > 0;
        if (atTop || atBottom) e.preventDefault();
      };
      scroller.addEventListener('touchmove', touchMoveGuard, { passive: false });
    }

    function detachScrollGuards(scroller) {
      if (!scroller) return;
      if (wheelGuard) scroller.removeEventListener('wheel', wheelGuard, { passive: false });
      if (touchStartGuard) scroller.removeEventListener('touchstart', touchStartGuard, { passive: true });
      if (touchMoveGuard) scroller.removeEventListener('touchmove', touchMoveGuard, { passive: false });
      wheelGuard = touchStartGuard = touchMoveGuard = null;
      touchStartY = 0;
    }

    // Keep JS in sync if something else toggles data-open
    const syncObserver = new MutationObserver(() => {
      if (modal.getAttribute('data-open') === 'true') {
        if (document.body.style.position !== 'fixed') lockPageScroll();
        attachScrollGuards(body);
      } else {
        if (document.body.style.position === 'fixed') unlockPageScroll();
        detachScrollGuards(body);
      }
    });
    syncObserver.observe(modal, { attributes: true, attributeFilter: ['data-open'] });
  }

  // Wire the trigger(s) to the matching modal by aria-controls (no-op if not found)
  qsa(document, '[data-area-calc-open]').forEach((btn) => {
    const targetId = btn.getAttribute('aria-controls');
    const modal = document.getElementById(targetId);
    if (!modal) return;
  });
})();
